/* Renders the compact manual single-leg order ticket for authenticated mobile users. */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {SymbolPicker} from '@qep/shared/src/components/SymbolPicker';
import {getUserAccountsFromTraderDB} from '@qep/shared/src/services/accounts';
import {placeOrder} from '@qep/shared/src/services/orders';
import {type Product} from '@qep/shared/src/utils/Product';
import {type OrderRecord, type TraderDBAccount} from '@qep/types/src';
import {
  type MobileOrderDraft,
  type MobileOrderFieldErrors,
  type MobileOrderSubmission,
  type MobileOrderType,
  type MobileOrderSide,
} from '../../mobile-order-types.js';
import {
  buildMobileOrderPayload,
  findMobileTradableAccounts,
  validateMobileOrderDraft,
} from '../../mobile-order-utils.js';

type MobileTradePageProps = {
  orders: MobileOrderSubmission[];
  onOrderSubmitted: (order: MobileOrderSubmission) => void;
};

type MobileSegmentTone = 'buy' | 'sell' | 'info';

const initialDraft: MobileOrderDraft = {
  product: undefined,
  accountId: undefined,
  side: 'buy',
  quantity: '1',
  orderType: 'limit',
  limitPrice: '',
  timeInForce: 'gtc',
};

/** Returns a concise display symbol from the selected product. */
function getProductDisplaySymbol(product?: Product): string {
  return product?.symbolDetails?.exchangeSymbol ?? product?.exchangeSymbol ?? 'Order';
}

/** Returns a readable error message from unknown service failures. */
function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Creates a session-local submission record that powers the compact order status list. */
function createSubmission({
  draft,
  status,
  message,
  serviceOrder,
}: {
  draft: MobileOrderDraft;
  status: MobileOrderSubmission['status'];
  message?: string;
  serviceOrder?: OrderRecord;
}): MobileOrderSubmission {
  const clientId = `mobile-${Date.now()}`;
  const orderId = serviceOrder?.orderId ?? serviceOrder?.clientOrderId ?? clientId;
  const accountLabel = draft.accountId ?? 'No account';

  return {
    id: orderId,
    clientId,
    symbol: getProductDisplaySymbol(draft.product),
    accountLabel,
    side: draft.side,
    quantity: Number.parseFloat(draft.quantity) || 0,
    orderType: draft.orderType,
    limitPrice: draft.orderType === 'limit' ? draft.limitPrice : undefined,
    status,
    timestamp: new Date().toISOString(),
    message,
    serviceOrder,
  };
}

/** Builds segmented button classes from active state and optional semantic color tone. */
function getSegmentedButtonClassName(isActive: boolean, tone?: MobileSegmentTone): string {
  return [
    isActive ? 'active' : '',
    tone ? `mobile-tone-${tone}` : '',
  ].filter(Boolean).join(' ');
}

/** Renders a two-option segmented control while preserving regular button semantics for tests and touch users. */
function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{value: T; label: string; tone?: MobileSegmentTone}>;
  onChange: (value: T) => void;
}) {
  return (
    <div className='mobile-field'>
      <span className='mobile-field-label'>{label}</span>
      <div className='mobile-segmented-control'>
        {options.map(option => (
          <button
            key={option.value}
            type='button'
            className={getSegmentedButtonClassName(value === option.value, option.tone)}
            aria-pressed={value === option.value}
            onClick={() => {
              onChange(option.value);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Renders the compact mobile trade ticket and submits valid orders through the shared order service. */
const MobileTradePage: React.FC<MobileTradePageProps> = ({orders, onOrderSubmitted}) => {
  const [draft, setDraft] = useState<MobileOrderDraft>(initialDraft);
  const [accounts, setAccounts] = useState<TraderDBAccount[]>([]);
  const [accountLoadError, setAccountLoadError] = useState<string | undefined>(undefined);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    /** Loads TraderDB accounts once so account filtering can happen locally after symbol selection. */
    async function loadAccounts() {
      try {
        const traderAccounts = await getUserAccountsFromTraderDB();
        if (!cancelled) {
          setAccounts(traderAccounts);
          setAccountLoadError(undefined);
        }
      } catch (error) {
        if (!cancelled) {
          setAccountLoadError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAccounts(false);
        }
      }
    }

    void loadAccounts();
    return () => {
      cancelled = true;
    };
  }, []);

  const tradableAccounts = useMemo(
    () => findMobileTradableAccounts(accounts, draft.product),
    [accounts, draft.product],
  );
  const errors: MobileOrderFieldErrors = validateMobileOrderDraft(draft);
  const selectedSymbol = getProductDisplaySymbol(draft.product);
  const latestOrder = orders[0];
  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    if (!draft.product) {
      return;
    }

    const hasSelectedAccount = tradableAccounts.some(account => account.id === draft.accountId);
    if (!hasSelectedAccount) {
      setDraft(previous => ({
        ...previous,
        accountId: tradableAccounts[0]?.id,
      }));
    }
  }, [draft.accountId, draft.product, tradableAccounts]);

  const updateDraft = useCallback((changes: Partial<MobileOrderDraft>) => {
    setDraft(previous => ({...previous, ...changes}));
    setSubmitMessage(undefined);
  }, []);

  const handleSymbolChange = useCallback((products: Product[]) => {
    const [product] = products;
    updateDraft({
      product,
      accountId: undefined,
    });
  }, [updateDraft]);

  const handleSubmit = useCallback(async () => {
    setSubmitMessage(undefined);
    if (hasErrors || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = buildMobileOrderPayload(draft, accounts);
      const serviceOrder = await placeOrder(payload);
      const acceptedOrder = createSubmission({
        draft,
        status: 'accepted',
        serviceOrder,
        message: 'Order accepted.',
      });
      onOrderSubmitted(acceptedOrder);
      setSubmitMessage('Order accepted.');
    } catch (error) {
      const message = getErrorMessage(error);
      const rejectedOrder = createSubmission({
        draft,
        status: 'rejected',
        message,
      });
      onOrderSubmitted(rejectedOrder);
      setSubmitMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [accounts, draft, hasErrors, isSubmitting, onOrderSubmitted]);

  const submitLabel = `${draft.side === 'buy' ? 'Buy' : 'Sell'} ${selectedSymbol}`;

  return (
    <article className='mobile-trade-page'>
      <header className='mobile-page-header'>
        <div>
          <p className='mobile-eyebrow'>Manual Order</p>
          <h1>Trade</h1>
        </div>
        {latestOrder && <span className={`mobile-status-chip ${latestOrder.status}`}>{latestOrder.status}</span>}
      </header>

      <section className='mobile-ticket-panel' aria-label='Mobile manual order ticket'>
        <div className='mobile-symbol-field'>
          <label className='mobile-field-label'>Symbol</label>
          <SymbolPicker
            mode='single'
            enabledFilters={['Future']}
            placeholder='Search symbol'
            className='mobile-symbol-picker'
            onChange={handleSymbolChange}
          />
          {draft.product && <div className='mobile-selected-symbol'>{selectedSymbol}</div>}
          {errors.symbol && <p className='mobile-error-text'>{errors.symbol}</p>}
        </div>

        <div className='mobile-field'>
          <label htmlFor='mobile-account'>Account</label>
          <select
            id='mobile-account'
            value={draft.accountId ?? ''}
            disabled={isLoadingAccounts || !draft.product}
            onChange={event => {
              updateDraft({accountId: event.target.value || undefined});
            }}
          >
            <option value=''>{isLoadingAccounts ? 'Loading accounts...' : 'Select account'}</option>
            {tradableAccounts.map(account => (
              <option key={account.id} value={account.id}>{account.description || account.code}</option>
            ))}
          </select>
          {errors.account && <p className='mobile-error-text'>{errors.account}</p>}
          {accountLoadError && <p className='mobile-error-text'>{accountLoadError}</p>}
        </div>

        <SegmentedControl<MobileOrderSide>
          label='Side'
          value={draft.side}
          options={[
            {value: 'buy', label: 'Buy', tone: 'buy'},
            {value: 'sell', label: 'Sell', tone: 'sell'},
          ]}
          onChange={side => {
            updateDraft({side});
          }}
        />

        <div className='mobile-field'>
          <label htmlFor='mobile-quantity'>Quantity</label>
          <input
            id='mobile-quantity'
            type='number'
            min='0'
            step='1'
            value={draft.quantity}
            onChange={event => {
              updateDraft({quantity: event.target.value});
            }}
          />
          {errors.quantity && <p className='mobile-error-text'>{errors.quantity}</p>}
        </div>

        <SegmentedControl<MobileOrderType>
          label='Type'
          value={draft.orderType}
          options={[{value: 'market', label: 'Market'}, {value: 'limit', label: 'Limit'}]}
          onChange={orderType => {
            updateDraft({orderType});
          }}
        />

        {draft.orderType === 'limit' && (
          <div className='mobile-field'>
            <label htmlFor='mobile-limit-price'>Limit Price</label>
            <input
              id='mobile-limit-price'
              type='number'
              min='0'
              step='0.01'
              value={draft.limitPrice}
              onChange={event => {
                updateDraft({limitPrice: event.target.value});
              }}
            />
            {errors.limitPrice && <p className='mobile-error-text'>{errors.limitPrice}</p>}
          </div>
        )}

        <div className='mobile-field'>
          <label htmlFor='mobile-tif'>TIF</label>
          <select
            id='mobile-tif'
            value={draft.timeInForce}
            onChange={event => {
              updateDraft({timeInForce: event.target.value === 'rth' ? 'rth' : 'gtc'});
            }}
          >
            <option value='rth'>RTH</option>
            <option value='gtc'>GTC</option>
          </select>
        </div>

        <div className='mobile-quote-strip' aria-label='Market quote summary'>
          <span>Bid --</span>
          <span>Ask --</span>
        </div>

        {submitMessage && <div className='mobile-submit-message'>{submitMessage}</div>}

        <button
          type='button'
          className={`mobile-submit-button ${draft.side}`}
          disabled={hasErrors || isSubmitting}
          onClick={() => {
            void handleSubmit();
          }}
        >
          {isSubmitting ? 'Submitting...' : submitLabel}
        </button>
      </section>
    </article>
  );
};

export default MobileTradePage;
